import { useEffect, useState } from 'react'
import { SidePanel } from '@/components/SidePanel'
import { Picker } from '@/components/Picker'
import { RunTelemetry } from '@/components/RunTelemetry'
import { useBoardData } from '@/components/useBoardData'
import type { BoardTelemetry } from '@/lib/runs'
import { queryPath, type BoardJob, type PoolNode, type PoolSummary } from '@/lib/task-runs'
import { TaskRolloutCards, PageButtons } from './TaskRolloutCards'

export function RemiTelemetry({ window, onWindowChange }: { window: string; onWindowChange: (value: string) => void }) {
  const state = useBoardData<BoardTelemetry>('/eng/runs/telemetry')
  useEffect(() => {
    const timer = globalThis.setInterval(() => { if (!document.hidden) void state.reload() }, 15000)
    return () => globalThis.clearInterval(timer)
  }, [state.reload])
  return <div className="remi-telemetry">
    {state.error && <p className="note err">{state.error}</p>}
    {state.data ? <RunTelemetry telemetry={state.data} window={window} onWindowChange={onWindowChange} /> : !state.error && <p className="muted">Loading telemetry…</p>}
  </div>
}

function NodeHistory({ poolId, nodeId, range, onSelect }: { poolId: number; nodeId: number; range: string;
  onSelect: (job: { experiment_id: string; job_id: string; attempt_id: string | null }) => void }) {
  const [offset, setOffset] = useState(0)
  const state = useBoardData<{ jobs: BoardJob[]; next_offset: number | null }>(queryPath(`/eng/runs/pools/${poolId}/jobs`, { machine: nodeId, window: range, offset }))
  useEffect(() => { const timer = globalThis.setInterval(() => { if (!document.hidden) void state.reload() }, 10000); return () => globalThis.clearInterval(timer) }, [state.reload])
  return <section className="panel-section"><h3>Rollout history on this machine</h3>
    {state.error && <p className="note err">{state.error}</p>}
    {state.data && <><TaskRolloutCards jobs={state.data.jobs} onSelect={onSelect} refresh={() => void state.reload()} />
      <PageButtons offset={offset} next={state.data.next_offset} onChange={setOffset} /></>}
  </section>
}

export function PoolPanel({ pool, view, status, window, machine, onMachine, onStatus, onClose, onSelect }: {
  pool?: PoolSummary; view: string; status?: string; window: string; onClose: () => void
  onSelect: (job: { experiment_id: string; job_id: string; attempt_id: string | null }) => void
  machine?: string; onMachine: (value?: string) => void
  onStatus: (value?: string) => void
}) {
  const [offset, setOffset] = useState(0)
  useEffect(() => { setOffset(0) }, [pool?.id, view, status, window, machine])
  const path = queryPath(`/eng/runs/pools/${pool?.id}/${view}`, { status: machine ? undefined : status, window, offset, machine })
  const state = useBoardData<{ jobs?: BoardJob[]; nodes?: PoolNode[]; next_offset: number | null }>(path)
  useEffect(() => {
    const timer = globalThis.setInterval(() => { if (!document.hidden) void state.reload() }, view === 'telemetry' ? 15000 : 10000)
    return () => globalThis.clearInterval(timer)
  }, [view, state.reload])
  const noun = view === 'nodes' ? 'machines' : view
  const nodeLabels: Record<string, string> = { requested: 'Starting', running: 'Working', idle: 'Idle', unreachable: 'Unreachable', terminated: 'Stopped' }
  const selected = state.data?.nodes?.find(n => String(n.machine_id) === machine)
  return <SidePanel label={view === 'telemetry' ? 'REMI telemetry' : `${pool?.name ?? 'Pool'} ${noun}`} onClose={onClose}
    head={<div>{selected && <button className="btn" onClick={() => onMachine()}>← Machines</button>}
      <span className="eyebrow">Execution · {noun}</span><h2>{view === 'telemetry' ? 'Telemetry' : pool?.name ?? 'Pool'}</h2>
      {!selected && <Picker label="Pool detail status" value={status ?? ''} onChange={value => onStatus(value || undefined)}
        options={[{ value: '', label: view === 'nodes' ? 'All machines' : 'All jobs' },
          ...(view === 'nodes' ? ['idle','running','requested','unreachable','terminated'] : ['queued','running','completed','failed','terminated'])
            .map(value => ({ value, label: view === 'nodes' ? nodeLabels[value] : value }))]} />}</div>}>
    {state.error && <p className="note err">{state.error}</p>}
    {!state.data && !state.error && <p className="muted">Loading…</p>}
    {view === 'jobs' && state.data && <>
      <TaskRolloutCards jobs={state.data.jobs ?? []} onSelect={onSelect} refresh={() => void state.reload()} />
      {!state.data.jobs?.length && <p className="empty">No matching jobs.</p>}
    </>}
    {view === 'nodes' && state.data && (selected ? <section className="panel-section">
      <h3>{selected.instance_id ?? `Starting machine ${selected.machine_id}`}</h3>
      <p>{selected.instance_type} · {nodeLabels[selected.category]}{selected.draining ? ' · Draining (not accepting new jobs)' : ''}</p>
      <p>{selected.busy}/{selected.slots} slots in use · Last heartbeat {selected.age_seconds == null ? 'not received' : `${selected.age_seconds}s ago`}</p>
      {selected.reason && <p className="note">{selected.reason}</p>}
      <h3>Assigned jobs</h3>
      {selected.jobs.map(j => <button className="pool-node-row" key={`${j.experiment_id}/${j.job_id}`} onClick={() => onSelect(j)}>
        <strong>{j.task_id}</strong><span>{j.model}</span><span>Attempt {j.attempt_id}</span>
      </button>)}
      {!selected.jobs.length && <p className="muted">No active assignments.</p>}
      {pool && <NodeHistory poolId={pool.id} nodeId={selected.machine_id} range={window} onSelect={onSelect} />}
    </section> : <div>
      {(state.data.nodes ?? []).map(n => <button className="pool-node-row" key={n.machine_id} onClick={() => onMachine(String(n.machine_id))}>
        <strong>{n.instance_id ?? `Starting machine ${n.machine_id}`}</strong><span>{n.instance_type}</span>
        <span>{nodeLabels[n.category]}{n.draining ? ' · Draining' : ''}</span><span>{n.busy}/{n.slots} slots in use</span>
      </button>)}
      {!state.data.nodes?.length && <p className="empty">No matching machines.</p>}
    </div>)}
    {view !== 'telemetry' && !selected && state.data && <PageButtons offset={offset} next={state.data.next_offset} onChange={setOffset} />}
  </SidePanel>
}
